import React, { useEffect, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { purchaseOrderService } from "../../utils/services/purchaseOrderService";
import PurchaseOrderModal from "../../components/purchase-order-modal";
import MasterPurchaseOrderList from "../../components/master-purchase-order-list";
import CustomPagination from "../../components/pagination";
import buildQuery from "../../utils/query-builder";
import { masterPOFilterInitialState } from "../../utils/filter-config";
import CustomTypography from "../../globalStyles/fonts";
import ImportIcon from "../../globalStyles/icons/importIcon";
import { isMobile } from "../../globalStyles/mobile";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };
    default:
      return state;
  }
}

const MasterPurchaseOrder = () => {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    masterPOFilterInitialState,
  );
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [purchaseOrderList, setPurchaseOrderList] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [page, setPage] = React.useState(0);
  const [dataCount, setDataCount] = useState(filterData.limit);
  const [loading, setLoading] = useState(false);
  const [fetchData, setFetchData] = useState(0);
  const clientId = localStorage.getItem("client_id");
  const navigate = useNavigate();
  const theme = useTheme();
  const primary_color = theme.palette?.primary?.main || "#D13438";

  const updateFilterData = () => {
    localStorage.setItem(
      "master-po-filter-data",
      JSON.stringify({
        filters: filterData.columns.map((x) => ({
          key: x.key,
          values: x.filter,
        })),
        limit: filterData.limit,
      }),
    );
  };

  useEffect(() => {
    const data = localStorage.getItem("master-po-filter-data");
    if (data) {
      localStorage.removeItem("master-po-filter-data");
      dispatch({ type: "EXISTING_DATA", data: JSON.parse(data) });
    }
  }, []);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  const fetchPurchaseOrder = async () => {
    setLoading(true);
    try {
      const response = await purchaseOrderService.fetch(
        buildQuery(filterData, page),
        clientId,
        "",
      );
      setPurchaseOrderList(response.data);
      setDataCount(response?.total);
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrder();
  }, [page, filterData, fetchData]);

  return (
    <>
      <DashboardContainer
        filterButton={false}
        hideUploadButton
        showRefreshButton
        header={"Master Purchase Orders"}
        title={"+ Add"}
        onClickAddButton={() => setOpenModal(true)}
        setViewMode={setViewMode}
        onClickRefresh={() => setFetchData(fetchData + 1)}
        hideFilterClient
        headerButtons={
          clientId ? (
            isMobile() ? (
              <IconButton
                onClick={() => {
                  updateFilterData();
                  navigate("/master-purchase-order-import");
                }}
              >
                <ImportIcon fill={primary_color} />
              </IconButton>
            ) : (
              <Button
                size="small"
                style={{ textTransform: "none" }}
                variant="contained"
                onClick={() => {
                  updateFilterData();
                  navigate("/master-purchase-order-import");
                }}
              >
                <ImportIcon fill="#FFF" />
                <CustomTypography fontSize="14px" fontWeight={500} color="#FFFFFF">
                  Import
                </CustomTypography>
              </Button>
            )
          ) : null
        }
      >
        <PurchaseOrderModal
          selectedPurchaseOrder={selectedPurchaseOrder}
          handleClose={() => {
            setOpenModal(false);
            setSelectedPurchaseOrder(null);
          }}
          open={openModal}
          onAddData={() => setFetchData(fetchData + 1)}
        />

        <MasterPurchaseOrderList
          dataCount={dataCount}
          currentPage={page}
          itemsPerPage={filterData.limit}
          setPage={setPage}
          onDeleteData={() => setFetchData(fetchData + 1)}
          onEditData={(e) => {
            setSelectedPurchaseOrder(e);
            setOpenModal(true);
          }}
          data={purchaseOrderList}
          viewMode={viewMode}
          selectedPurchaseOrder={selectedPurchaseOrder}
          setSelectedPurchaseOrder={setSelectedPurchaseOrder}
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          loading={loading}
          setLoading={setLoading}
        />
        <CustomPagination
          page={page}
          rowsPerPage={filterData.limit}
          setPage={setPage}
          setRowsPerPage={(e) =>
            dispatch({ type: "UPDATE_LIMIT", payload: e })
          }
          dataCount={dataCount}
          disableNext={dataCount <= (page + 1) * filterData.limit}
        />
      </DashboardContainer>
    </>
  );
};

export default MasterPurchaseOrder;
